import React, { useState } from 'react'
import { createContext } from 'lib/createContext'
import { useCompanyDetailsByIdQuery } from 'queries/company/useCompanyDetailsByIdQuery'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useShiftsQuery } from 'queries/shifts/useShiftsQuery'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useTimeRangeQueryState } from '../hooks/useTimeRangeQueryState'
import { format } from 'date-fns'
import { SelectableColumns } from '../components/Shifts/ShiftsTable/ColumnsFilter'
import { useSelectedColumnsQueryState } from '../hooks/useSelectedColumnsQueryState'
import { useCompanyLocationsQuery } from 'queries/company/useCompanyLocationsQuery'
import { CompanyLocation } from 'api/company/location'
import { useDeleteCompanyInviteMutation } from 'queries/company'
import { UseMutationOptions } from '@tanstack/react-query'

type CompanyData = NonNullable<
  ReturnType<typeof useCompanyDetailsByIdQuery>['data']
>

type CompanyDetailContextState = {
  state: CompanyData & {
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    shifts: {
      dataUpdatedAt: number
      isFetching: boolean
      isError: boolean
      error: string
      data: NonNullable<ReturnType<typeof useShiftsQuery>['data']>
      selectedColumnsFilters: SelectableColumns[]
      pagination: {
        pageIndex: number
        pageSize: number
      }
      timeRangeFilters: {
        type?: 'past' | 'ongoing' | 'upcoming'
        startDate?: Date
        endDate?: Date
      }
    }
    locations: {
      data: CompanyLocation[]
      isLoading: boolean
      isError: boolean
      error: string
      dataUpdatedAt: number
      isFetching: boolean
    }
    locationEditModal: {
      location?: CompanyLocation
    }
    deleteInviteMutationState: {
      isPending: boolean
      isError: boolean
      error: string
    }
  }
  actions: {
    refetch: () => void
    shifts: {
      refetch: () => void
      setTimeRangeFilters: ReturnType<typeof useTimeRangeQueryState>[1]
      setPagination: ReturnType<typeof usePaginationQueryState>[1]
      setSelectedColumnsFilters: ReturnType<
        typeof useSelectedColumnsQueryState
      >[1]
    }
    locations: {
      refetch: () => void
    }
    locationEditModal: {
      openModal: (location: CompanyLocation) => void
      closeModal: () => void
    }
    deleteInvite: (
      inviteId: number,
      options?: UseMutationOptions<void, Error, number>
    ) => void
  }
}

const [Context, useCompanyDetailContextValue] =
  createContext<CompanyDetailContextState>({
    name: 'CompanyDetailContext',
  })

export const CompanyDetailProvider = ({
  children,
  companyId,
}: {
  children: React.ReactNode
  companyId: number
}) => {
  const [pagination, setPagination] = usePaginationQueryState({
    defaultPageSize: 10,
    urlKeys: { pageIndex: 'shifts-page', pageSize: 'shifts-page-size' },
  })
  const [timeRangeFilters, setTimeRangeFilters] = useTimeRangeQueryState()
  const [selectedColumnsFilters, setSelectedColumnsFilters] =
    useSelectedColumnsQueryState()
  const [locationEditModalLocation, setLocationEditModalLocation] = useState<
    CompanyLocation | undefined
  >(undefined)

  const {
    data,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyError,
    dataUpdatedAt: companyDataUpdatedAt,
    refetch: companyRefetch,
    isFetching: isCompanyFetching,
  } = useCompanyDetailsByIdQuery(companyId)

  const {
    data: locations = [],
    isLoading: isLocationsLoading,
    isError: isLocationsError,
    error: locationsError,
    dataUpdatedAt: locationsDataUpdatedAt,
    refetch: refetchLocations,
    isFetching: isLocationsFetching,
  } = useCompanyLocationsQuery(companyId)

  const {
    data: shifts,
    isPending: isShiftsLoading,
    isError: isShiftsError,
    error: shiftsError,
    dataUpdatedAt: shiftsDataUpdatedAt,
    refetch: refetchShifts,
    isFetching: isShiftsFetching,
  } = useShiftsQuery({
    rangeType: timeRangeFilters.type,
    startDate: timeRangeFilters.startDate
      ? format(timeRangeFilters.startDate, 'yyyy-MM-dd')
      : undefined,
    endDate: timeRangeFilters.endDate
      ? format(timeRangeFilters.endDate, 'yyyy-MM-dd')
      : undefined,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    companyIds: [companyId],
  })

  const deleteInviteMutation = useDeleteCompanyInviteMutation(companyId)

  const isLoading = isCompanyLoading || isShiftsLoading

  if (isLoading || !data || !shifts) {
    return <Loading />
  }

  if (isCompanyError) {
    // eslint-disable-next-line no-console
    console.error(companyError)
    return (
      <Message variant="error">
        Sorry, something went wrong. Please reach out to eng support if this
        persist
      </Message>
    )
  }

  const value: CompanyDetailContextState = {
    state: {
      ...data,
      dataUpdatedAt: companyDataUpdatedAt,
      isFetching: isCompanyFetching,
      isError: isCompanyError,
      error: parseErrorMessage(companyError),
      shifts: {
        dataUpdatedAt: shiftsDataUpdatedAt,
        isFetching: isShiftsFetching,
        isError: isShiftsError,
        error: parseErrorMessage(shiftsError),
        data: shifts,
        pagination,
        timeRangeFilters,
        selectedColumnsFilters,
      },
      locations: {
        data: locations,
        isLoading: isLocationsLoading,
        isError: isLocationsError,
        error: parseErrorMessage(locationsError),
        dataUpdatedAt: locationsDataUpdatedAt,
        isFetching: isLocationsFetching,
      },
      locationEditModal: {
        location: locationEditModalLocation,
      },
      deleteInviteMutationState: {
        isPending: deleteInviteMutation.isPending,
        isError: deleteInviteMutation.isError,
        error: parseErrorMessage(deleteInviteMutation.error),
      },
    },
    actions: {
      refetch: companyRefetch,
      shifts: {
        refetch: refetchShifts,
        setTimeRangeFilters,
        setPagination,
        setSelectedColumnsFilters,
      },
      locations: {
        refetch: refetchLocations,
      },
      locationEditModal: {
        openModal: (location: CompanyLocation) =>
          setLocationEditModalLocation(location),
        closeModal: () => setLocationEditModalLocation(undefined),
      },
      deleteInvite: (
        inviteId: number,
        options?: UseMutationOptions<void, Error, number>
      ) => deleteInviteMutation.mutate(inviteId, options),
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useCompanyDetailContextValue }
